import { Injectable } from '@angular/core';
import { ApiService } from '../api.service';
import { HandleFileService } from './handle-file.service';
import { Property } from '../models/property';
import { Observable } from 'rxjs';
import { map, debounceTime } from 'rxjs/operators';
import { TransactionType, TransactionTypes } from '../models/transactions-enum';
import { DataObject } from '../interfaces/data-objects';

@Injectable({
  providedIn: 'root'
})
export class PropertyService {

  propertiesEndPoint = 'properties';

  constructor(
    private api: ApiService,
    private handleFile: HandleFileService
  ) { }

  createProperty(transactionId: number, isCIQFlow: boolean | null = null): Observable<Property> {
    let params: any = { country: null };
    if (isCIQFlow === true || isCIQFlow === false) {
      params = { ...params, 'is_ciq_flow': isCIQFlow };
    }
    return this.api.post(`/api/transactions/${transactionId}/${this.propertiesEndPoint}/`, params)
      .pipe(
        map((response: any) => {
          if (response) {
            return response;
          }
        })
      );
  }

  getProperties(transactionId: number): Observable<any> {
    return this.api.get(`/api/transactions/${transactionId}/${this.propertiesEndPoint}/`);
  }

  saveProperty(transactionId: number, property: Property): Observable<any> {
    return this.api.put(`/api/transactions/${transactionId}/${this.propertiesEndPoint}/${property.id}/`, property).pipe(debounceTime(500));
  }

  deleteProperty(transactionId: number, propertyId: number): Observable<any> {
    return this.api.delete(`/api/transactions/${transactionId}/${this.propertiesEndPoint}/${propertyId}/`);
  }

  uploadPropertiesFile(fileToUpload: any, transactionId: number): Observable<any> {
    return this.handleFile.post(`/api/transactions/${transactionId}/${this.propertiesEndPoint}/upload/`, fileToUpload, null, 'response');
  }

  getProperty(transactionId: number, propertyId: number) {
    return this.api.get(`/api/transactions/${transactionId}/${this.propertiesEndPoint}/${propertyId}/`);
  }

  deleteProperties(transactionId: number) {
    return this.api.delete(`/api/transactions/${transactionId}/delete/`);
  }

  getPropertyTypeMixCustomizations(property: number): Observable<DataObject> {
    return this.api.get(`/api/properties/${property}/property-type-mix-customizations/`);
  }

  createPropertyTypeMixCustomization(payload: { property_type: string; weighting: string }, property): Observable<DataObject> {
    return this.api.post(`/api/properties/${property}/property-type-mix-customizations/`, payload);
  }

  deletePropertyTypeMixCustomization(propertyTypeId: number, property): Observable<DataObject> {
    return this.api.delete(`/api/properties/${property}/property-type-mix-customizations/${propertyTypeId}/`);
  }

  updatePropertyTypeMixCustomization(payload: DataObject, propertyTypeId: number, property: number): Observable<DataObject> {
    return this.api.put(`/api/properties/${property}/property-type-mix-customizations/${propertyTypeId}/`, payload);
  }

  getSizeByType(property: DataObject): string {
    let returnValue = '';
    if (property && property.property_type) {
      const propType = property.property_type.toLowerCase();
      switch (propType) {
        case 'land':
          returnValue = property.land + ' ' + property.land_measurement;
          break;
        case 'hotel':
          returnValue = property.rooms + ' Rooms';
          break;  
        case 'mixed_use_portfolio':
          returnValue = '-';
          break;
        case 'residential':
          returnValue = property.units + ' Units';
          break;
        default:
          returnValue = property.size + ' ' + property.size_measurement;
          break;
      }
    }
    return returnValue;
  }

  // To be used on all children components except Address Form
  // For the Address Form we just need to pass in the isCEPIntegration flag
  getCEPIntegration(properties: DataObject[], propertyIndex: number, isCEPIntegration: boolean): boolean {
    if (properties && properties[propertyIndex]) {
      if (properties[propertyIndex].lease_comp_property_id && isCEPIntegration) {
        return true;
      }
    }
    return false;
  }
}
